import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "../../../../context/OrganizationContext";
import { dynamicFormList } from "../../../../services/dynamicFormService";
import { toast } from "react-toastify";
import CheckoutForm from "../../../elements/tabContent/CheckoutForm/CheckoutForm";
import Card from "../../../elements/Card";
import { useOutletContext } from "react-router-dom";
import { t } from "i18next";

const GlobalCheckoutFormEvent = ({ readOnly = false, type }) => {
  const { event, eventLoading, eventOrganizer } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const queryClient = useQueryClient();

  const { data: globalData } = useQuery({
    queryKey: ["global-checkoutForm"],
    queryFn: async () => {
      const res = await dynamicFormList({
        sortQuery: "ordering",
        type: "global-checkout",
      });
      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs || {};
    },
  });

  const { data: sectionData } = useQuery({
    queryKey: [
      "checkoutForm",
      selectedOrganization,
      ...(type === "event"
        ? [event?.data?._id, "event-checkout"]
        : ["organization-checkout"]),
      type,
    ],
    ...(type === "event"
      ? {
          enabled:
            globalData?.data?.length > 0 ? true : false && !!event?.data?._id,
        }
      : {
          enabled: globalData?.data?.length > 0 ? true : false,
        }),
    queryFn: async () => {
      const res = await dynamicFormList({
        sortQuery: "ordering",
        organization: selectedOrganization,
        ...(type === "event"
          ? {
              event: event?.data?._id,
              type: "event-checkout",
            }
          : {
              type: "organization-checkout",
            }),
      });

      if (res?.status !== 200) toast.warning(res?.message);
      return res?.docs || {};
    },
  });

  function onOrderingResponse(res) {
    queryClient.setQueryData(
      ["checkoutForm", selectedOrganization],
      (prevState) => {
        if (!prevState?.data) return prevState;

        const newState = JSON.parse(JSON.stringify(prevState));
        const reorderedFieldsMap = {};

        res.forEach((question, index) => {
          reorderedFieldsMap[question.id] = {
            ...question,
            ordering: index,
          };
        });

        newState.data = newState.data.map((form) => ({
          ...form,
          sectionDetail: form.sectionDetail.map((section) => ({
            ...section,
            relatedField: section.relatedField
              .map((field) => {
                const reorderedField = reorderedFieldsMap[field._id];
                return reorderedField
                  ? { ...field, ordering: reorderedField.ordering }
                  : field;
              })
              .sort((a, b) => (a.ordering || 0) - (b.ordering || 0)),
          })),
        }));

        return newState;
      }
    );
  }

  function onAddEditQuestionResponse(res) {
    if (res?.status === 200 || res?.status === 201) {
      if (res?.status === 201) {
        // New form created
        queryClient.setQueryData(
          ["checkoutForm", selectedOrganization],
          (state) => {
            if (state) {
              return { ...state, data: [...(state.data || []), res.data] };
            } else {
              // If no state exists, create new state
              return { data: [res.data] };
            }
          }
        );
      }

      if (res?.status === 200) {
        // Existing form updated
        queryClient.setQueryData(
          ["checkoutForm", selectedOrganization],
          (state) => {
            if (state?.data) {
              const updatedData = state.data
                .sort((a, b) => (a.ordering || 0) - (b.ordering || 0))
                .map((item) =>
                  item._id === res.data._id ? { ...item, ...res.data } : item
                );

              return { ...state, data: updatedData };
            } else {
              // If no data exists, create new data array
              return { data: [res.data] };
            }
          }
        );
      }

      // Refresh the query to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: ["checkoutForm", selectedOrganization],
      });
    } else {
      toast.warning(res?.message || res?.error);
    }
  }

  function onDeleteQuestionResponse(res) {
    queryClient.setQueryData(
      ["checkoutForm", selectedOrganization],
      (state) => {
        if (!state) return state;

        const stateData = JSON.parse(JSON.stringify(state.data));
        const updatedData = stateData.map((section) => ({
          ...section,
          sectionDetail: section.sectionDetail.map((detail) => ({
            ...detail,
            relatedField: detail.relatedField.filter(
              (field) => field._id !== res?.id
            ),
          })),
        }));

        return { ...state, data: updatedData };
      }
    );
  }

  function assembleFormData({ globalData, sectionData }) {
    const sec_data_length = sectionData?.data?.length || 0;

    if (sec_data_length > 0) {
      const data = sectionData?.data?.map((form) => {
        if (form?.sectionDetail?.length > 1) {
          return form;
        } else {
          return {
            ...form,
            sectionDetail: [
              form?.sectionDetail[0],
              globalData?.data?.[0]?.sectionDetail?.[1],
            ],
          };
        }
      });
      return data;
    } else {
      return globalData?.data;
    }
  }

  return (
    <Card
      title={
        <div>
          {t(`${type === "event" ? "Event" : "Global"} checkout form`)}{" "}
          <span className="text-sm font-medium">
            {type === "event"
              ? `(${t("Apply only this event")})`
              : `(${t("Apply on every events of this organization")})`}
          </span>
        </div>
      }
      className={"px-3 md:px-6 py-4 md:py-8 bg-white"}
    >
      <div className="space-y-6">
        {assembleFormData({ sectionData, globalData })?.map((form, i) =>
          form?.sectionDetail?.map((item, j) => (
            <CheckoutForm
              formTitle={t(
                j === 0 ? "Billing Information" : "Attendee Information"
              )}
              form={form}
              formFor={j === 0 ? "billing" : "attendee"}
              key={item?._id}
              {...(Array.isArray(sectionData?.data) &&
              sectionData?.data?.length > 0
                ? {
                    sectionData: item,
                    hasSection: false,
                  }
                : { hasSection: false })}
              globalData={globalData?.data?.[0]?.sectionDetail?.[0]}
              onOrderingResponse={onOrderingResponse}
              onAddEditQuestionResponse={onAddEditQuestionResponse}
              onDeleteQuestionResponse={onDeleteQuestionResponse}
              readOnly={readOnly}
              crudPayload={{ event: event?.data?._id, type: "event-checkout" }}
            />
          ))
        )}
      </div>
    </Card>
  );
};

export default GlobalCheckoutFormEvent;
